import { type FunctionComponent, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  MenuBase,
  MenuItem,
  MenuHeader,
  Divider,
  NavigationBar,
} from '@retailer-platform/shared-components/src/tds'
import { ChevronDownIcon, ChevronUpIcon } from '@instacart/ids-core'
import { usePartnerContext } from '../../../utils/contexts/partner/PartnerContext.hooks'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { useAbsoluteRouteMatch } from '../../../utils/routing/useAbsoluteRouteMatch.hooks'
import { url as resolveUrlPattern } from '../../../utils/parsing/url'
import SelectPartner from '../../common/select-partner/SelectPartner'
import { useTopBarMenu } from './TopBarMenuContext'

const CHEVRON_SIZE = 16

// Open question (per plan): the v2 nav populates Settings and User management as
// collapsible parent groups via NavEntryPoint.UserMenuSettings /
// NavEntryPoint.UserMenuUserManagement. The screenshot renders them flat. For now we
// flat-link to the canonical destinations and defer the collapsible-population work.
const SETTINGS_ROUTE = 'settings'
const USER_MANAGEMENT_ROUTE = 'partner-account-management-list'

const useRouteHref = (routeName: string): string | undefined => {
  const { routesByName } = useCoreContext()
  const routeMatch = useAbsoluteRouteMatch()

  return useMemo(() => {
    const pattern = routesByName[routeName]
    if (!pattern) return undefined
    return resolveUrlPattern(pattern, (routeMatch?.params as Record<string, string>) ?? {})
  }, [routeName, routesByName, routeMatch?.params])
}

export const TdsPartnerMenu: FunctionComponent = () => {
  const intl = useIntl()
  const { isOpen, setIsOpen } = useTopBarMenu('partner')
  const [showPartnerSelector, setShowPartnerSelector] = useState(false)
  const { partnerName } = usePartnerContext()

  const settingsHref = useRouteHref(SETTINGS_ROUTE)
  const userManagementHref = useRouteHref(USER_MANAGEMENT_ROUTE)

  const Chevron = isOpen ? ChevronUpIcon : ChevronDownIcon

  const trigger = (
    <NavigationBar.Button
      aria-label={intl.formatMessage({ id: 'app.nav.partnerMenu.label' })}
      trailingIcon={<Chevron size={CHEVRON_SIZE} fill="currentColor" />}
      onPress={() => setIsOpen(!isOpen)}
    >
      {partnerName}
    </NavigationBar.Button>
  )

  return (
    <>
      <MenuBase target={trigger} isOpen={isOpen} onMenuStateChange={setIsOpen} position="bottomEnd">
        <MenuHeader title={partnerName} />
        <MenuItem
          text={intl.formatMessage({ id: 'app.nav.partnerMenu.switchPartner' })}
          onClick={() => {
            setIsOpen(false)
            setShowPartnerSelector(true)
          }}
        />
        <Divider />
        <MenuItem
          text={intl.formatMessage({ id: 'app.nav.partnerMenu.settings' })}
          href={settingsHref}
        />
        <MenuItem
          text={intl.formatMessage({ id: 'app.nav.partnerMenu.userManagement' })}
          href={userManagementHref}
        />
      </MenuBase>
      {showPartnerSelector && <SelectPartner handleClose={() => setShowPartnerSelector(false)} />}
    </>
  )
}
